import type React from 'react'
import { useState, useEffect, useCallback } from 'react'

import { DateTime } from 'luxon'

import type { HistoryWindow } from '@/components/charts/history/useHistoryNavigation'
import { Alert } from '@/components/ui'
import { useApi } from '@/hooks'
import AssetHistoryChart from '@/insights/assets/historyGraph/AssetHistoryChart'
import type { AssetHistory, TempUnits } from '@/insights/assets/historyGraph/types'
import type { AssetWindowState } from '@/insights/common/assetHistoryWindow'
import { buildQuery } from '@/insights/common/queryUtils'
import { getUserTimezone } from '@/insights/common/timezone'

const CHART_PERIOD_HOURS = 24

interface AssetTemperatureGraphProps {
  assetId: number
  companyId: number | undefined
  units: TempUnits
  assetWindow: AssetWindowState
  /** The graph picker, drawn at the left of the date bar. */
  picker: React.ReactNode
}

/** The Graphs tab's Temperature graph: the asset's `/assets/history` telemetry. */
export default function AssetTemperatureGraph({
  assetId,
  companyId,
  units,
  assetWindow,
  picker,
}: AssetTemperatureGraphProps) {
  const { api } = useApi()
  const [chartData, setChartData] = useState<AssetHistory>()
  const [chartError, setChartError] = useState('')
  const [isChartLoading, setIsChartLoading] = useState(false)
  // Default to today's calendar day (00:00 → next 00:00) rather than a rolling
  // last-24-hours window. Navigation steps a full 24h from that baseline.
  const [chartFrom, setChartFrom] = useState<DateTime>(() => DateTime.now().setZone(getUserTimezone()).startOf('day'))

  const fetchChartData = useCallback(
    async (window: HistoryWindow): Promise<AssetHistory | undefined> => {
      if (!companyId) return undefined
      const query = buildQuery({
        companyId: companyId,
        date: 'custom',
        from: window.from.toISO() ?? '',
        periodHours: String(window.to.diff(window.from, 'hours').hours),
        includeAlertPins: 'true',
      })
      return api.simpleGet<AssetHistory>({
        path: `/assets/history/${assetId}?${query}`,
        origin: 'AssetTemperatureGraph#fetchChartData',
      })
    },
    [api, assetId, companyId]
  )

  const reloadChart = useCallback(
    async (_: typeof api, window: HistoryWindow): Promise<AssetHistory | undefined> => {
      // IMPORTANT: don't push the result back into chartData/chartFrom — the chart
      // owns its window internally, and changing the `data` prop here would make it
      // treat the fetch as a reconfigure and discard the user's range.
      try {
        return await fetchChartData(window)
      } catch {
        return undefined
      }
    },
    [fetchChartData]
  )

  useEffect(() => {
    const initialFrom = DateTime.now().setZone(getUserTimezone()).startOf('day')
    setChartFrom(initialFrom)
    setChartData(undefined)
    setChartError('')
    setIsChartLoading(true)
    fetchChartData({ from: initialFrom, to: initialFrom.plus({ hours: CHART_PERIOD_HOURS }) })
      .then((data) => setChartData(data))
      .catch(() => setChartError('Unable to load telemetry graph data.'))
      .finally(() => setIsChartLoading(false))
  }, [fetchChartData])

  return (
    <div className="relative">
      {chartData ? (
        <AssetHistoryChart
          data={chartData}
          from={chartFrom}
          periodHours={CHART_PERIOD_HOURS}
          alertMarkers={undefined}
          reload={reloadChart}
          units={units}
          initialWindow={assetWindow.period}
          onWindowChange={assetWindow.setPeriod}
          leading={picker}
        />
      ) : (
        // No chart, so no date bar to carry the picker: keep it reachable on its own.
        <div className="flex flex-col gap-2 px-4">
          {picker}
          {chartError ? (
            <Alert type="error" message={chartError} />
          ) : !isChartLoading ? (
            <Alert type="info" message="No telemetry data available." />
          ) : (
            <div className="h-[300px]" />
          )}
        </div>
      )}
      {isChartLoading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/70">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
          <p className="text-sm text-gray-600">Loading temperature data…</p>
        </div>
      )}
    </div>
  )
}
